<template>
  <Grid gtc="repeat(4, 1fr 48px) 1fr">
    <Grid
      v-for="(section, sIndex) of workflow"
      :key="section.title"
      align-items="start"
      align-content="start"
      class="section"
    >
      <div class="title text-overline-1 text-grey2">{{ section.title }}</div>
      <DotArrow v-if="sIndex === workflow.length - 1" />
      <LineArrow v-else />

      <Grid gap="var(--r-space-md)" class="q-mt-sm" :class="[sIndex !== workflow.length - 1 ? 'q-mr-xxl' : 'q-mr-sm']">
        <Task
          v-for="(task, tIndex) of section.tasks"
          :ref="task.id"
          :key="task.name"
          :period-id="periodId"
          v-bind="task"
          @update-count="updateCount(sIndex, tIndex, $event, task.id)"
        />
      </Grid>
    </Grid>
  </Grid>
</template>

<script>
import * as apiTo from '@/modular-api'
import {
  subscriptionWorkflow,
  dashboardWorkflow,
  subscriptionTaskMap,
  dashboardTaskMap
} from '@/reusable/workflow/shared'
/// Libraries
import { mapMutations } from 'vuex'
// Components
import Task from '@/reusable/workflow/Task'
import LineArrow from '@/reusable/svg-icons/LineArrow'
import DotArrow from '@/reusable/svg-icons/DotArrow'

export default {
  components: {
    Task,
    LineArrow,
    DotArrow
  },

  props: {
    periodId: Number,
    from: {
      type: String,
      validator: v => ['dashboard', 'subscription'].includes(v)
    }
  },

  data: () => ({
    workflow: null
  }),

  created() {
    this.setTasks()

    this.$root.$on('open-task', taskName => this.$refs[taskName][0].openTaskDialog())
    this.$root.$on('refresh-workflow', this.setTasks)
  },

  beforeDestroy() {
    this.$root.$off('open-task')
    this.$root.$off('refresh-workflow')
  },

  methods: {
    ...mapMutations('subscription-show', ['setSubscription', 'updatePeriod']),

    setTasks() {
      if (this.from === 'subscription') {
        this.workflow = subscriptionWorkflow()
        this.tasksIndexMap = subscriptionTaskMap
      } else {
        this.workflow = dashboardWorkflow()
        this.tasksIndexMap = dashboardTaskMap
      }

      this.fetchAndSetTaskCount(Object.keys(this.tasksIndexMap), true)
    },

    fetchAndSetTaskCount(taskNames, setDependencies = false) {
      /* Gets all tasks count requests and sets the count and dependencies on each task in the `this.workflow` */
      const fnName = this.from === 'subscription' ? 'GET_TASK_COUNT_FOR_SUB_PERIOD' : 'GET_TASK_COUNT'
      const promises = taskNames.map(taskName => apiTo[fnName](taskName, this.periodId))
      Promise.all(promises).then(tasks => {
        taskNames.forEach((taskName, index) => {
          const { sIndex, tIndex } = this.tasksIndexMap[taskName]
          this.workflow[sIndex].tasks[tIndex].count = tasks[index].data.result.count
          if (setDependencies) this.workflow[sIndex].tasks[tIndex].dependencies = tasks[index].data.result.dependencies
        })
      })
    },

    updateCount(sectionIndex, taskIndex, count, taskId) {
      if (this.workflow[sectionIndex].tasks[taskIndex].count === count) return

      if (this.from === 'subscription') this.updateMilestoneData(taskId)

      let deps = this.workflow[sectionIndex].tasks[taskIndex].dependencies

      if (count !== null) {
        this.workflow[sectionIndex].tasks[taskIndex].count = count
      } else {
        deps = [taskId, ...deps]
      }

      if (deps.length) {
        deps.forEach(dep => {
          const { sIndex, tIndex } = this.tasksIndexMap[dep]
          this.workflow[sIndex].tasks[tIndex].count = null
        })
      }
      this.fetchAndSetTaskCount(deps)
    },

    updateMilestoneData(task) {
      this.$root.$emit('refresh-milestones-data', {
        periodId: this.periodId,
        milestone: task
      })

      if (['decisions', 'vendor-payments'].includes(task)) {
        apiTo.GET_SUBSCRIPTION(this.$route.params.id).then(({ data }) => {
          this.setSubscription(data.result)
          data.result.current && this.updatePeriod({ ...data.result.current })
          data.result.next && this.updatePeriod({ ...data.result.next })
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.workflow {
  .section:not(:last-child) {
    grid-column: span 2;
  }
}
</style>
